import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import {Table, Card, Input, Typography, Tag, Space, message, Button, DatePicker} from 'antd';
import {SearchOutlined, PlusOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {Trip} from "../../types";
import api from '../../api';
import moment from 'moment';
const {Title} = Typography;
const {RangePicker} = DatePicker;


const TripsPage: React.FC = () => {
    const [trips, setTrips] = useState<Trip[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [dateRange, setDateRange] = useState<[moment.Moment | null, moment.Moment | null] | null>(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTrips = async () => {
            setLoading(true);
            try {
                const params: any = {
                    page: pagination.current,
                    page_size: pagination.pageSize,
                };

                if (searchText) {
                    params.search = searchText;
                }

                if (dateRange?.[0] && dateRange?.[1]) {
                    params.date_after = dateRange[0].format("YYYY-MM-DD");
                    params.date_before = dateRange[1].format("YYYY-MM-DD");
                    params.departure_type = "checkin";
                }

                const response = await api.get('/daia/trips/', {params});

                setTrips(response.data.results || []);
                setTotal(response.data.count || []);
            } catch (error) {
                console.error('Error fetching trips:', error);
                message.error('Failed to fetch trips');
            } finally {
                setLoading(false);
            }
        };

        fetchTrips();
    }, [pagination.current, pagination.pageSize, searchText, dateRange]);

    const today = moment().startOf('day');

    const liveChipsInPlay = trips
        .filter(trip =>
            (trip.final_balance === null || trip.final_balance === "Pending") &&
            moment(trip.checkin_date).isSameOrBefore(today)
        )
        .reduce((total, trip) => {
            const buyin = parseFloat(String(trip.buyin_amount ?? "0"));
            const rebuys = parseFloat(String(trip.rebuys ?? "0"));
            return total + buyin + rebuys;
        }, 0);

    const columns: ColumnsType<Trip> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            align: 'center',
            render: (id: number) => <Tag color="blue">{id}</Tag>,
        },
        {
            title: "Customer Name",
            key: "customer_name",
            render: (_, record) => (
                <strong>{record.customer ? `${record.customer.first_name} ${record.customer.last_name}` : "N/A"}</strong>
            ),
        },
        {
            title: "MMID",
            dataIndex: ["customer", "mmid"],
            key: "mmid",
            render: (mmid: string | null) => <Tag color="gold">{mmid || "N/A"}</Tag>,
        },
        {
            title: "Agent",
            key: "agent",
            render: (_, record) => (
                <span>{record.agent ? `${record.agent.first_name} ${record.agent.last_name}` : "Casino Club"}</span>
            ),
        },
        {
            title: 'Check-in Date',
            dataIndex: 'checkin_date',
            key: 'checkin_date',
            render: (date: string) => <Tag color="green">{moment(date).format('YYYY-MM-DD')}</Tag>,
        },
        {
            title: 'Check-out Date',
            dataIndex: 'checkout_date',
            key: 'checkout_date',
            render: (date: string | null) =>
                date ? <Tag color="red">{moment(date).format('YYYY-MM-DD')}</Tag> : <Tag color="gray">N/A</Tag>,
        },
        {
            title: 'Bonus Structure',
            dataIndex: 'bonus_structure',
            key: 'bonus_structure',
            align: 'center',
            render: (structure: string) => (
                <Tag color={structure === "ROL" ? "purple" : "geekblue"}>{structure}</Tag>
            ),
        },
        {
            title: 'Buy-in',
            dataIndex: 'buyin_amount',
            key: 'buyin_amount',
            align: 'right',
            render: (amount: string) => <Tag color="cyan"><span
                style={{fontWeight: 600}}>₩</span> {parseFloat(amount).toLocaleString()}</Tag>,
        },
        {
            title: 'Rebuys',
            dataIndex: 'rebuys',
            key: 'rebuys',
            align: 'right',
            render: (amount: string) => <Tag color="purple"><span
                style={{fontWeight: 600}}>₩</span> {parseFloat(amount).toLocaleString()}</Tag>,
        },
        {
            title: 'Final Balance',
            dataIndex: 'final_balance',
            key: 'final_balance',
            align: 'right',
            render: (amount: string | null) => (
                <Tag color={amount === "Pending" || amount === null ? "gray" : "blue"}>
                    <span
                        style={{fontWeight: 600}}>₩</span> {amount === "Pending" || amount === null ? "Pending" : parseFloat(amount).toLocaleString()}
                </Tag>
            ),
        },
        {
            title: 'Win/Loss',
            dataIndex: 'win_loss',
            key: 'win_loss',
            align: 'right',
            render: (amount: string | null) => (
                <Tag
                    color={amount === "Pending" || amount === null ? "gray" : parseFloat(amount) >= 0 ? "green" : "red"}>
                    <span
                        style={{fontWeight: 600}}>₩</span> {amount === "Pending" || amount === null ? "Pending" : parseFloat(amount).toLocaleString()}
                </Tag>
            ),
        },
        {
            title: 'Bonus Amount',
            dataIndex: 'bonus_amount',
            key: 'bonus_amount',
            align: 'right',
            render: (amount: string | null) =>
                amount ? (
                    <Tag color="gold">
                        <span style={{fontWeight: 600}}>₩</span> {parseFloat(amount).toLocaleString()}
                    </Tag>
                ) : (
                    <Tag color="gray">N/A</Tag>
                ),
        },
        {
            title: 'Exchange',
            key: 'exchange',
            render: (_, record) =>
                record.exchange_currency ? (
                    <Tag color="blue">
                        {record.exchange_currency} {record.locked_exchange_rate ? '(Locked)' : ''}
                    </Tag>
                ) : (
                    <Tag color="gray">N/A</Tag>
                ),
        },
    ];

    return (
        <Card
            title={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                    }}
                >
                    <div>
                        <Title level={2} style={{margin: 0}}>
                            Trips
                        </Title>
                        <div
                            style={{
                                fontSize: 16,
                                color: "#1890ff",
                                fontWeight: 600,
                                margin: "4px 0",
                            }}
                        >
                            Live Chips in Play: ₩{liveChipsInPlay.toLocaleString()}
                        </div>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined/>}
                        onClick={() => navigate("/daia/trips/new")}
                    >
                        New Trip
                    </Button>
                </div>
            }
            style={{margin: "16px", padding: "16px"}}
        >
            <Space
                style={{marginBottom: 16, width: "100%"}}
                direction="vertical"
                size="middle"
            >
                <Space wrap>
                    <Input
                        placeholder="Search by Customer or Agent Name..."
                        prefix={<SearchOutlined/>}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{width: 300}}
                    />
                    <RangePicker
                        onChange={(dates) =>
                            setDateRange(dates as [moment.Moment | null, moment.Moment | null])
                        }
                        style={{width: 280}}
                        placeholder={["Check-in Start", "Check-in End"]}
                    />
                </Space>
            </Space>

            <Table
                scroll={{x: "max-content"}}
                loading={loading}
                dataSource={trips.map((trip) => ({ ...trip, key: `trip-${trip.id}` }))}
                columns={columns}
                rowKey="id"
                bordered
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) =>
                        setPagination({current: page, pageSize}),
                }}
                onRow={(record) => ({
                    onClick: () => navigate(`/daia/trips/${record.id}`),
                    style: {
                        backgroundColor:
                            record.final_balance !== null && record.final_balance !== "Pending"
                                ? "#f0f0f0"
                                : "#ffffff",
                        cursor: "pointer",
                        transition: "background-color 0.3s ease",
                    },
                })}
            />
        </Card>

    );
};

export default TripsPage;
